import React, { useState, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../utils';

// Table Context for state management
const TableContext = React.createContext();

export const useTable = () => {
  const context = React.useContext(TableContext);
  if (!context) {
    throw new Error('useTable must be used within a TableProvider');
  }
  return context;
};

// Table Provider Component
export const TableProvider = ({ children, data = [], columns = [], onSort, onFilter }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debug logging
  console.log('📊 TableProvider received:', {
    dataLength: data.length,
    data: data,
    columns: columns,
    searchTerm,
    currentPage,
    pageSize
  });

  // Filtered and sorted data
  const processedData = useMemo(() => {
    console.log('🔄 Processing data:', { 
      originalDataLength: data.length, 
      searchTerm, 
      filterConfig, 
      sortConfig 
    });
    
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        columns.some(column => {
          const value = column.accessor ? item[column.accessor] : '';
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
      console.log('🔍 After search filter:', filtered.length);
    }

    // Apply column filters
    Object.entries(filterConfig).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(item => {
          const itemValue = item[key];
          return String(itemValue).toLowerCase().includes(String(value).toLowerCase());
        });
        console.log('🔍 After column filter:', filtered.length);
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      // Find the column to check for sortKey
      const column = columns.find(col => col.accessor === sortConfig.key);
      const sortKey = column?.sortKey || sortConfig.key;
      
      filtered.sort((a, b) => {
        const aValue = a[sortKey];
        const bValue = b[sortKey];
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      console.log('🔄 After sorting:', filtered.length);
    }

    console.log('✅ Final processed data:', filtered);
    return filtered;
  }, [data, searchTerm, filterConfig, sortConfig, columns]);

  // Pagination
  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = processedData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  console.log('📄 Pagination:', {
    totalPages,
    currentPage,
    pageSize,
    paginatedDataLength: paginatedData.length,
    paginatedData: paginatedData
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    onSort?.(key, sortConfig.direction);
  };

  const handleFilter = (key, value) => {
    setFilterConfig(prev => ({
      ...prev,
      [key]: value
    }));
    onFilter?.(key, value);
  };

  const contextValue = {
    data: paginatedData,
    allData: processedData,
    columns,
    sortConfig,
    filterConfig,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    handleFilter,
    totalItems: processedData.length
  };

  console.log('🎯 TableProvider context value:', {
    dataLength: contextValue.data.length,
    allDataLength: contextValue.allData.length,
    totalItems: contextValue.totalItems,
    totalPages: contextValue.totalPages,
    currentPage: contextValue.currentPage
  });

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
};

// Table Container - must be a <table> element
export const Table = ({ className, children, ...props }) => (
  <table className={cn("w-full", className)} {...props}>
    {children}
  </table>
);

// Table Header - wrapper for search and header styling (renders outside table)
export const TableHeader = ({ className, children, ...props }) => (
  <div className={cn("bg-muted border-b border-border", className)} {...props}>
    {children}
  </div>
);

// Table Search Bar
export const TableSearch = ({ placeholder = "Search...", className, ...props }) => {
  const { searchTerm, setSearchTerm } = useTable();
  
  return (
    <div className={cn("p-4 border-b border-border", className)} {...props}>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-background text-foreground border-input focus:ring-ring focus:border-ring"
        />
      </div>
    </div>
  );
};

// Table Head - must be direct child of <table>
export const TableHead = ({ className, children, ...props }) => (
  <thead className={cn("bg-muted border-b border-border", className)} {...props}>
    {children}
  </thead>
);

// Table Body - must be direct child of <table>
export const TableBody = ({ className, children, ...props }) => (
  <tbody className={cn("bg-background divide-y divide-border", className)} {...props}>
    {children}
  </tbody>
);

// Table Row
export const TableRow = ({ className, children, ...props }) => (
  <tr className={cn("hover:bg-muted transition-colors", className)} {...props}>
    {children}
  </tr>
);

// Table Header Cell
export const TableHeaderCell = ({ 
  column, 
  className, 
  children, 
  sortable = true,
  ...props 
}) => {
  const { sortConfig, handleSort } = useTable();
  const isSorted = sortConfig.key === column?.accessor;
  const sortDirection = isSorted ? sortConfig.direction : null;

  return (
    <th
      className={cn(
        "px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider",
        sortable && "cursor-pointer hover:bg-muted",
        className
      )}
      onClick={sortable && column?.accessor ? () => handleSort(column.accessor) : undefined}
      {...props}
    >
      <div className="flex items-center space-x-1">
        <span>{children || column?.label}</span>
        {sortable && column?.accessor && (
          <div className="flex flex-col">
            <ChevronUpIcon 
              className={cn(
                "h-3 w-3",
                isSorted && sortDirection === 'asc' ? 'text-primary' : 'text-muted-foreground'
              )} 
            />
            <ChevronDownIcon 
              className={cn(
                "h-3 w-3 -mt-1",
                isSorted && sortDirection === 'desc' ? 'text-primary' : 'text-muted-foreground'
              )} 
            />
          </div>
        )}
      </div>
    </th>
  );
};

// Table Cell
export const TableCell = ({ className, children, ...props }) => (
  <td className={cn("px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-foreground", className)} {...props}>
    {children}
  </td>
);

// Table Footer with Pagination
export const TableFooter = ({ className, ...props }) => {
  const { 
    currentPage, 
    setCurrentPage, 
    totalPages, 
    pageSize, 
    setPageSize, 
    totalItems 
  } = useTable();

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className={cn("bg-background px-4 py-3 flex items-center justify-between border-t border-border", className)} {...props}>
      <div className="flex-1 flex justify-between sm:hidden">
        <Button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          variant="outline"
          size="sm"
        >
          Previous
        </Button>
        <Button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          variant="outline"
          size="sm"
        >
          Next
        </Button>
      </div>
      
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center space-x-2">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </p>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="ml-2 text-sm border border-input rounded-md px-2 py-1 bg-background text-foreground"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            variant="outline"
            size="sm"
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            variant="outline"
            size="sm"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

// Status Badge Component
export const StatusBadge = ({ status, className, ...props }) => {
  const getStatusStyle = (status) => {
    const styles = {
      active: { backgroundColor: '#10B981', color: 'white' },
      inactive: { backgroundColor: '#6B7280', color: 'white' },
      pending: { backgroundColor: '#F59E0B', color: 'white' },
      completed: { backgroundColor: 'var(--brand-primary)', color: 'white' },
      cancelled: { backgroundColor: '#EF4444', color: 'white' },
      prospect: { backgroundColor: 'var(--brand-primary)', color: 'white' },
      archived: { backgroundColor: '#6B7280', color: 'white' }
    };
    return styles[status] || styles.inactive;
  };

  const label = typeof status === 'string' && status.length > 0
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : status;

  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
        className
      )}
      style={getStatusStyle(status)}
      {...props}
    >
      {props.children || label}
    </span>
  );
};

// Priority Badge Component
export const PriorityBadge = ({ priority, className, ...props }) => {
  const getPriorityStyle = (priority) => {
    const styles = {
      low: { backgroundColor: '#6B7280', color: 'white' },
      medium: { backgroundColor: 'var(--brand-accent-dynamic)', color: 'white' },
      high: { backgroundColor: '#F97316', color: 'white' },
      urgent: { backgroundColor: '#EF4444', color: 'white' }
    };
    return styles[priority] || styles.medium;
  };

  return (
    <span
      className={cn(
        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
        className
      )}
      style={getPriorityStyle(priority)}
      {...props}
    >
      {props.children || priority}
    </span>
  );
};

// Avatar Component for Table
export const TableAvatar = ({ name, src, className, ...props }) => {
  const initials = name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase() || '?';

  return (
    <div className={cn("flex items-center", className)} {...props}>
      {src ? (
        <img
          className="h-8 w-8 rounded-full"
          src={src}
          alt={name}
        />
      ) : (
        <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
          {initials}
        </div>
      )}
    </div>
  );
};

// All components are already exported as named exports above
